import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Chip,
    Stack,
    IconButton,
    Divider,
    Tooltip,
    LinearProgress,
    Card,
    CardContent,
    Button,
    Badge
} from '@mui/material';
import {
    Description as DocumentIcon,
    Visibility as ViewIcon,
    Download as DownloadIcon,
    Share as ShareIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    Note as NoteIcon,
    Security as SecurityIcon
} from '@mui/icons-material';
import authService from '../services/auth.service';
import SensitiveDataSettings from './SensitiveDataSettings';
import DocumentNotes from './DocumentNotes';

interface SearchResult {
    _id: string;
    fileSyncId: {
        fileName: string;
        fileSize: number;
        mimeType: string;
        machineId: string;
    };
    category: string;
    score?: number;
    highlight?: {
        text: string[];
        title?: string[];
    };
    matchedFields?: string[];
    processingCompletedAt?: string;
    ocrConfidence?: number;
    isSensitive?: boolean;
    sensitiveDataCategory?: string;
    canAccess?: boolean;
    notes?: any[];
}

interface SearchResultsProps {
    results: SearchResult[];
    query: string;
    loading: boolean;
    onViewDocument: (id: string) => void;
    onShareDocument: (id: string) => void;
    onClose?: () => void;
    totalResults?: number;
    currentPage?: number;
    onRefresh?: () => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({
    results,
    query,
    loading,
    onViewDocument,
    onShareDocument,
    onClose,
    totalResults = 0,
    currentPage = 1,
    onRefresh
}) => {
    const [sensitiveSettingsOpen, setSensitiveSettingsOpen] = useState(false);
    const [sensitiveDocument, setSensitiveDocument] = useState<SearchResult | null>(null);
    const [notesOpen, setNotesOpen] = useState(false);
    const [notesDocumentId, setNotesDocumentId] = useState<string | null>(null);
    const [localResults, setLocalResults] = useState<SearchResult[]>(results);

    const currentUser = authService.getUser();
    const canEditSensitive = currentUser?.role === 'admin' || currentUser?.role === 'manager';

    React.useEffect(() => {
        setLocalResults(results);
    }, [results]);
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getCategoryColor = (
        category: string
    ): 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'default' => {
        const colors: { [key: string]: any } = {
            contracts: 'primary',
            invoices: 'success',
            reports: 'info',
            correspondence: 'secondary',
            forms: 'warning',
            other: 'default'
        };
        return colors[category] || 'default';
    };

    const highlightText = (text: string, query: string): React.ReactNode => {
        if (!query || !text) return text;

        // Escape special regex characters
        const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

        try {
            const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
            return (
                <>
                    {parts.map((part, index) =>
                        part.toLowerCase() === query.toLowerCase() ? (
                            <mark key={index} style={{ backgroundColor: '#ffeb3b', padding: '0 2px' }}>
                                {part}
                            </mark>
                        ) : (
                            part
                        )
                    )}
                </>
            );
        } catch (error) {
            // If regex fails, return text without highlighting
            console.error('Highlight error:', error);
            return text;
        }
    };

    if (loading) {
        return (
            <Paper sx={{ p: 3 }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    Searching documents...
                </Typography>
            </Paper>
        );
    }

    if (results.length === 0) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                    No results found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {query
                        ? 'Try adjusting your search criteria or using different keywords'
                        : 'No documents available'}
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            {/* Search summary */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="subtitle1">
                            {query ? (
                                <>
                                    Search Results for "<strong>{query}</strong>"
                                </>
                            ) : (
                                'All Documents'
                            )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Found {totalResults} document{totalResults !== 1 ? 's' : ''}
                        </Typography>
                    </Box>
                    {onClose && (
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            </Paper>

            {/* Results list */}
            <Stack spacing={2}>
                {localResults.map((result) => (
                    <Card key={result._id} elevation={2}>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-8">
                                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                                        <DocumentIcon sx={{ fontSize: 40, color: 'text.secondary', mt: 0.5 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="h6" gutterBottom>
                                                {highlightText(result.fileSyncId.fileName, query)}
                                            </Typography>

                                            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                                <Chip
                                                    label={result.category}
                                                    size="small"
                                                    color={getCategoryColor(result.category)}
                                                />
                                                <Chip
                                                    label={formatFileSize(result.fileSyncId.fileSize)}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                                {result.ocrConfidence && (
                                                    <Chip
                                                        label={`${result.ocrConfidence.toFixed(0)}% OCR`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                                {result.score && (
                                                    <Chip
                                                        label={`Score: ${result.score.toFixed(2)}`}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Stack>

                                            {/* Highlighted text snippets */}
                                            {result.highlight?.text && result.highlight.text.length > 0 && (
                                                <Paper sx={{ p: 1.5, bgcolor: 'grey.50', mb: 1 }}>
                                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                        ...{result.highlight.text[0]}...
                                                    </Typography>
                                                </Paper>
                                            )}

                                            <Typography variant="caption" color="text.secondary">
                                                {result.processingCompletedAt ? (
                                                    <>
                                                        Processed on{' '}
                                                        {new Date(result.processingCompletedAt).toLocaleDateString()}
                                                    </>
                                                ) : (
                                                    <>Processing status unknown</>
                                                )}
                                                {result.matchedFields && result.matchedFields.length > 0 && (
                                                    <> • Matched in: {result.matchedFields.join(', ')}</>
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </div>

                                <div className="col-span-12 md:col-span-4">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Tooltip
                                            title={
                                                canEditSensitive
                                                    ? 'Manage sensitivity settings'
                                                    : `${result.sensitiveDataCategory || 'Sensitive'} document`
                                            }
                                        >
                                            <IconButton
                                                onClick={
                                                    canEditSensitive
                                                        ? () => {
                                                              setSensitiveDocument(result);
                                                              setSensitiveSettingsOpen(true);
                                                          }
                                                        : undefined
                                                }
                                                color="error"
                                                disabled={!canEditSensitive}
                                            >
                                                <SecurityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {result.notes && result.notes.length > 0 ? (
                                            <Tooltip
                                                title={`${result.notes.length} note${result.notes.length > 1 ? 's' : ''}`}
                                            >
                                                <IconButton
                                                    onClick={() => {
                                                        setNotesDocumentId(result._id);
                                                        setNotesOpen(true);
                                                    }}
                                                >
                                                    <Badge badgeContent={result.notes.length} color="primary">
                                                        <NoteIcon />
                                                    </Badge>
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Add note">
                                                <IconButton
                                                    onClick={() => {
                                                        setNotesDocumentId(result._id);
                                                        setNotesOpen(true);
                                                    }}
                                                >
                                                    <NoteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="View document">
                                            <IconButton onClick={() => onViewDocument(result._id)} color="primary">
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Share document">
                                            <IconButton onClick={() => onShareDocument(result._id)}>
                                                <ShareIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Download document">
                                            <IconButton
                                                onClick={async () => {
                                                    try {
                                                        const api = authService.getApiInstance();
                                                        const response = await api.get(
                                                            `/documents/${result._id}/download`,
                                                            {
                                                                responseType: 'blob'
                                                            }
                                                        );

                                                        // Create a download link
                                                        const url = window.URL.createObjectURL(
                                                            new Blob([response.data])
                                                        );
                                                        const link = document.createElement('a');
                                                        link.href = url;
                                                        link.setAttribute('download', result.fileSyncId.fileName);
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        link.remove();
                                                        window.URL.revokeObjectURL(url);
                                                    } catch (error) {
                                                        console.error('Download failed:', error);
                                                    }
                                                }}
                                            >
                                                <DownloadIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            {/* Sensitive Data Settings Dialog */}
            {sensitiveDocument && (
                <SensitiveDataSettings
                    open={sensitiveSettingsOpen}
                    onClose={() => {
                        setSensitiveSettingsOpen(false);
                        setSensitiveDocument(null);
                    }}
                    document={sensitiveDocument}
                    onUpdate={() => {
                        // Update the local results to reflect changes
                        const updatedResults = localResults.map((r) =>
                            r._id === sensitiveDocument._id
                                ? {
                                      ...r,
                                      isSensitive: sensitiveDocument.isSensitive,
                                      sensitiveDataCategory: sensitiveDocument.sensitiveDataCategory
                                  }
                                : r
                        );
                        setLocalResults(updatedResults);
                    }}
                />
            )}

            {/* Document Notes Dialog */}
            {notesDocumentId && (
                <DocumentNotes
                    open={notesOpen}
                    onClose={() => {
                        setNotesOpen(false);
                        setNotesDocumentId(null);
                    }}
                    documentId={notesDocumentId}
                    onUpdate={async () => {
                        // Update note count locally without refreshing the entire search
                        try {
                            const api = authService.getApiInstance();
                            const response = await api.get(`/documents/${notesDocumentId}/notes`);
                            const noteCount = response.data.notes.length;
                            
                            // Update the local results to reflect the new note count
                            const updatedResults = localResults.map((result) =>
                                result._id === notesDocumentId
                                    ? { ...result, notes: response.data.notes }
                                    : result
                            );
                            setLocalResults(updatedResults);
                        } catch (error) {
                            console.error('Failed to fetch updated notes:', error);
                        }
                    }}
                />
            )}
        </Box>
    );
};

export default SearchResults;
